import React, { useRef, useState } from "react";
import { UIBrandedFareOrFlexTicket, UIFetchStatus, UIFlightData } from "@flights/types";
import { Button, Card, Popover, SkeletonLoader, Stack, Text, useTheme } from "@bookingcom/bui-react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import useGoToFlightDetails from "../../../../branded-fares/hooks/useGoToFlightDetails";
import Frame from "../../Frame";
import FareFeatures from "../../../../branded-fares/components/FareFeatures";
import FareFeaturesIcons from "../../../../branded-fares/components/FareFeatureIcons";
import FareCardPrice from "./FareCardPrice";
import styles from "./FareCard.desktop.module.css";
import {
  areExcludedFeaturesRequiredByAirline,
  areSellableFeaturesRequiredByAirline
} from "@flights/ancillaries-universal";
import { formatFareName } from "@flights/branded-fares";

type FareCardProps = {
  fareOffer: UIBrandedFareOrFlexTicket;
  baseOffer: UIFlightData;
  loadingStatus?: UIFetchStatus;
  index?: number;
  numberOfOffers?: number;
  hasSellableBag?: boolean;
  /* start - flights_web_flight_card_update_desktop */
  isFlightCardUpdate?: boolean;
  /* end - flights_web_flight_card_update_desktop */
};

const FareCardDesktop = ({
  baseOffer,
  fareOffer,
  loadingStatus,
  index,
  numberOfOffers,
  hasSellableBag,
  isFlightCardUpdate = false
}: FareCardProps) => {
  const i18n = useI18n();
  const theme = useTheme();
  const popoverRef = useRef(document.body);
  const goToFlightDetails = useGoToFlightDetails();
  const sellableFeaturesRequiredByAirline = areSellableFeaturesRequiredByAirline(baseOffer.segments);
  const excludedFeaturesRequiredByAirline = areExcludedFeaturesRequiredByAirline(baseOffer.segments);
  const [popoverShown, setPopoverShown] = useState(false);

  const { brandedFareInfo } = fareOffer;

  const renderedFeatures = (
    <FareFeatures
      brandedFareInfo={brandedFareInfo}
      sellableFeaturesRequiredByAirline={sellableFeaturesRequiredByAirline}
      excludedFeaturesRequiredByAirline={excludedFeaturesRequiredByAirline}
      showHeader={true}
      isPreCheckAndPay={true}
      areShown={popoverShown}
      travellers={baseOffer.travellerPrices.length}
      hasSellableBag={hasSellableBag}
    />
  );

  return (
    <Popover
      triggerType="hover"
      containerRef={popoverRef}
      onOpen={() => {
        setPopoverShown(true);
      }}
      onClose={() => {
        setPopoverShown(false);
      }}
    >
      <Popover.Trigger className={styles.popoverTrigger}>
        {(attributes) => (
          <Card className={styles.card}>
            <Frame
              attributes={{
                ...attributes,
                tabIndex: 0
              }}
              justifyContent="space-between"
            >
              <Text variant="strong_2" attributes={{ "data-fare-card-row": "title" }}>
                {loadingStatus === "loading" ? (
                  <Stack gap={6} direction="row">
                    <Stack.Item grow>
                      <SkeletonLoader variant="title" />
                    </Stack.Item>
                    <SkeletonLoader width={theme.units.spacing_12x} />
                  </Stack>
                ) : (
                  formatFareName(brandedFareInfo?.fareName)
                )}
              </Text>
              <Frame mt={2} data-fare-card-row="icons">
                <FareFeaturesIcons
                  brandedFareInfo={brandedFareInfo}
                  limit={6}
                  isCompact={true}
                  isPreCheckAndPay={true}
                />
              </Frame>
              <Frame mt={2}>
                <FareCardPrice
                  fareOffer={fareOffer}
                  baseOffer={baseOffer}
                  isBold={true}
                  isFlightCardUpdate={isFlightCardUpdate}
                />
              </Frame>
            </Frame>
            <Frame mt={2}>
              <Button
                wide={true}
                onClick={() => {
                  goToFlightDetails(fareOffer.token, baseOffer, index);
                }}
                variant="secondary"
                attributes={{
                  ...(index !== undefined && {
                    "aria-label": `${index + 1} of ${numberOfOffers}`
                  })
                }}
              >
                {i18n.trans(t("flights_apex_bf_sr_bf_card_cta"))}
              </Button>
            </Frame>
          </Card>
        )}
      </Popover.Trigger>
      {renderedFeatures ? (
        <Popover.Content
          attributes={{
            style: {
              width: 240,
              whiteSpace: "pre-wrap",
              overflow: "hidden"
            }
          }}
        >
          {renderedFeatures}
        </Popover.Content>
      ) : null}
    </Popover>
  );
};

export default FareCardDesktop;
